/**
 * Small reusable widget for the checkout — offers Google SSO when the shopper
 * is signed out, and shows a mini profile card (avatar + name/email) with a
 * "Not you?" link when they're signed in.
 */
import { LogIn, LogOut, ShoppingBag, Heart } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useShopperAuth } from "@/context/ShopperAuthContext";

export default function ShopperAuthCard() {
  const { shopper, loading, exchanging, signIn, signOut } = useShopperAuth();

  if (loading || exchanging) {
    return (
      <div className="border border-dashed rounded p-3 text-xs text-muted-foreground animate-pulse" data-testid="shopper-auth-loading">
        Checking sign-in…
      </div>
    );
  }

  if (shopper) {
    return (
      <div className="border rounded p-3 bg-muted/40 flex items-center gap-3" data-testid="shopper-auth-profile">
        {shopper.picture ? (
          <img src={shopper.picture} alt="" className="h-9 w-9 rounded-full object-cover border slh-no-img-style" referrerPolicy="no-referrer" />
        ) : (
          <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold">
            {(shopper.name || shopper.email || "?").charAt(0).toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate" data-testid="shopper-name">{shopper.name || shopper.email}</div>
          <div className="text-xs text-muted-foreground truncate" data-testid="shopper-email">{shopper.email}</div>
          <div className="flex items-center gap-3 mt-1">
            <Link
              to="/my-orders"
              className="text-xs text-primary hover:underline inline-flex items-center gap-1"
              data-testid="shopper-my-orders-link"
            >
              <ShoppingBag className="h-3 w-3" /> My orders
            </Link>
            <Link
              to="/my-favourites"
              className="text-xs text-primary hover:underline inline-flex items-center gap-1"
              data-testid="shopper-my-favourites-link"
            >
              <Heart className="h-3 w-3" /> My Favourites
            </Link>
          </div>
        </div>
        <button
          type="button"
          onClick={signOut}
          className="text-xs text-muted-foreground hover:text-red-600 flex items-center gap-1 transition-colors"
          data-testid="shopper-signout-btn"
          title="Sign out"
        >
          <LogOut className="h-3 w-3" /> Not you?
        </button>
      </div>
    );
  }

  return (
    <div className="border rounded p-3 flex items-center justify-between gap-3 bg-background" data-testid="shopper-auth-signin">
      <div className="text-sm">
        <div className="font-medium">Sign in for faster checkout</div>
        <div className="text-xs text-muted-foreground">We&apos;ll pre-fill your details automatically.</div>
      </div>
      <Button type="button" variant="outline" onClick={() => signIn()} data-testid="shopper-signin-btn" className="whitespace-nowrap">
        <LogIn className="h-4 w-4 mr-1" /> Sign in with Google
      </Button>
    </div>
  );
}
